import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  Avatar,
} from "@mui/material";
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Storage as StorageIcon,
  Visibility as VisibilityIcon,
  CheckCircleOutline as ApproveIcon,
  PlayArrow as PlayArrowIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  Code as CodeIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import type { RootState, AppDispatch } from "../../../store/Index";
import type { EndpointRequest, Priority } from "../types";
import { notifyRequestCompleted, getUnreadCount } from "../utils/notifications";
import apiService from "../../../service/Api";
import { updateRequest as updateRequestAction } from "../../../store/slice/EndpointRequestsSlice";
import { isBackendDev, isAdmin } from "../utils/permissions";
import {
  ModernMethodChip,
  ModernPriorityChip,
  ModernStatCard,
} from "./ModernUiComponents";

interface ApiErrorResponse {
  response?: {
    data?: {
      message?: string;
      detail?: string;
      [key: string]: unknown;
    };
  };
}

interface BackendDashboardProps {
  requests: EndpointRequest[];
  onRequestUpdate: (id: string, updates: Partial<EndpointRequest>) => void;
  onReloadRequests?: () => Promise<void>;
  onViewRequest: (request: EndpointRequest) => void;
}

export const BackendDashboard: React.FC<BackendDashboardProps> = ({
  requests,
  onRequestUpdate,
  onReloadRequests,
  onViewRequest,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const user = useSelector((state: RootState) => state.auth.user);
  const primaryColor = reduxTheme.primary_colors?.[400] || "#724E41";

  const [activeTab, setActiveTab] = useState<"requested" | "in_progress">("requested");
  const [searchTerm, setSearchTerm] = useState("");
  const [priorityFilter, setPriorityFilter] = useState<Priority | "all">("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<EndpointRequest | null>(null);
  const [endpointUrl, setEndpointUrl] = useState("");
  const [statusCode, setStatusCode] = useState<number | undefined>(undefined);
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [stats, setStats] = useState({
    requested: 0,
    inProgress: 0,
    completed: 0,
  });

  // Check if user is backend developer or admin based on permissions
  const userIsAdmin = isAdmin(user);
  const userIsBackendDev = isBackendDev(user);
  const userCanAccessBackend = userIsBackendDev || userIsAdmin;

  useEffect(() => {
    if (!userCanAccessBackend) return;

    const updateCount = () => {
      setNotificationCount(getUnreadCount("backend"));
    };

    updateCount();
    window.addEventListener(
      "endpoint-request-notification-count-updated",
      updateCount
    );

    return () => {
      window.removeEventListener(
        "endpoint-request-notification-count-updated",
        updateCount
      );
    };
  }, [userCanAccessBackend]);

  const loadStats = React.useCallback(async () => {
    if (!userCanAccessBackend) return;
    
    try {
      const response = await apiService.get<{
        total_requests: number;
        status_counts: {
          completed?: number;
          pending?: number;
          in_progress?: number;
          blocked?: number;
          cancelled?: number;
        };
      }>("/api/endpoint-request/endpoint-requests/statistics/");

      setStats({
        requested: response.data.status_counts.pending || 0,
        inProgress: response.data.status_counts.in_progress || 0,
        completed: response.data.status_counts.completed || 0,
      });
    } catch (error: unknown) {
      console.error("Error loading stats:", error);
      setStats({
        requested: requests.filter((r) => r.status === "pending").length,
        inProgress: requests.filter((r) => r.status === "in_progress").length,
        completed: requests.filter((r) => r.status === "completed").length,
      });
    }
  }, [userCanAccessBackend, requests]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (userCanAccessBackend && requests.length > 0) {
      loadStats();
    }
  }, [requests.length, userCanAccessBackend, loadStats]);

  useEffect(() => {
    if (!userCanAccessBackend) return;

    const handleNotification = (event: CustomEvent) => {
      const notification = event.detail;
      if (notification.type === "request_created") {
        toast.info(`New endpoint request: ${notification.component} - ${notification.task}`, {
          autoClose: 5000,
        });
      }
    };

    window.addEventListener("endpoint-request-notification", handleNotification as EventListener);
    return () => {
      window.removeEventListener("endpoint-request-notification", handleNotification as EventListener);
    };
  }, [userCanAccessBackend]);

  const filteredRequests = useMemo(() => {
    let filtered = [...requests];

    if (activeTab === "requested") {
      filtered = filtered.filter((r) => r.status === "pending");
    } else if (activeTab === "in_progress") {
      filtered = filtered.filter((r) => r.status === "in_progress");
    }

    if (priorityFilter !== "all") {
      filtered = filtered.filter((r) => r.priority === priorityFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.component.toLowerCase().includes(term) ||
          r.task.toLowerCase().includes(term) ||
          r.lead.toLowerCase().includes(term) ||
          r.createdBy?.toLowerCase().includes(term) ||
          r.endpoint?.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [requests, activeTab, priorityFilter, searchTerm]);

  const paginatedRequests = filteredRequests.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleStartWork = async (request: EndpointRequest) => {
    if (!request.id) {
      toast.error("Request ID is missing");
      return;
    }

    try {
      await apiService.patch<{
        status: string;
      }>(`/api/endpoint-request/endpoint-requests/${request.id}/`, {
        status: "in_progress",
      });

      dispatch(updateRequestAction({
        id: request.id,
        updates: {
          status: "in_progress",
          updatedAt: new Date().toISOString(),
        },
      }));

      onRequestUpdate(request.id, {
        status: "in_progress",
        updatedAt: new Date().toISOString(),
      });

      if (onReloadRequests) {
        await onReloadRequests();
      }

      toast.success(`Started working on: ${request.component}`);
    } catch (error: unknown) {
      console.error("Error updating request status:", error);
      const apiError = error as ApiErrorResponse;
      const errorMessage =
        apiError.response?.data?.message ||
        apiError.response?.data?.detail ||
        "Failed to update request status. Please try again.";
      toast.error(errorMessage);
    }
  };

  const handleOpenApproveDialog = (request: EndpointRequest) => {
    setSelectedRequest(request);
    setEndpointUrl(request.endpoint || "");
    setStatusCode(request.statusCode);
    setOpenApproveDialog(true);
  };

  const handleApproveAndComplete = async () => {
    if (!selectedRequest) return;

    if (!selectedRequest.id) {
      toast.error("Request ID is missing");
      return;
    }

    if (!endpointUrl || endpointUrl.trim() === "") {
      toast.error("Please enter the API endpoint URL");
      return;
    }

    try {
      const completionDate = new Date().toISOString();
      // Make API call to update status to completed with endpoint URL and status code
      await apiService.patch<{
        status: string;
        endpoint_url: string;
      }>(`/api/endpoint-request/endpoint-requests/${selectedRequest.id}/`, {
        status: "completed",
        endpoint_url: endpointUrl.trim(),
        status_code: statusCode || null,
        actual_completion_date: completionDate,
      });

      const updates: Partial<EndpointRequest> = {
        status: "completed",
        endpoint: endpointUrl.trim(),
        statusCode: statusCode,
        actualCompletionDate: new Date().toISOString().split("T")[0],
        approvedBy: user?.username || "backend",
        approvedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      dispatch(updateRequestAction({
        id: selectedRequest.id,
        updates,
      }));

      onRequestUpdate(selectedRequest.id, updates);
      
      if (onReloadRequests) {
        await onReloadRequests();
      }
      
      notifyRequestCompleted({
        id: selectedRequest.id,
        component: selectedRequest.component,
        task: selectedRequest.task,
        endpoint: endpointUrl.trim(),
        approvedBy: user?.username || "backend",
        createdBy: selectedRequest.createdBy,
      });
      
      toast.success(`Endpoint request completed: ${selectedRequest.component}`);

      setOpenApproveDialog(false);
      setSelectedRequest(null);
      setEndpointUrl("");
      setStatusCode(undefined);

      window.dispatchEvent(
        new CustomEvent("endpoint-request-completed", {
          detail: {
            requestId: selectedRequest.id,
            endpoint: endpointUrl.trim(),
            component: selectedRequest.component || "Unknown",
          },
        })
      );
    } catch (error: unknown) {
      console.error("Error completing request:", error);
      const apiError = error as ApiErrorResponse;
      const errorMessage =
        apiError.response?.data?.message ||
        apiError.response?.data?.detail ||
        "Failed to complete request. Please try again.";
      toast.error(errorMessage);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value) && value > 0) {
      setRowsPerPage(value);
      setPage(0);
    }
  };

  if (!userCanAccessBackend) {
    return null;
  }

  return (
    <Box sx={{ mb: 4 }}>
      {/* Hero Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3.5,
          mb: 3.5,
          borderRadius: "20px",
          background: `linear-gradient(135deg, ${primaryColor}1F 0%, ${primaryColor}0D 100%)`,
          border: `1px solid ${primaryColor}30`,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.04)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
          <Box
            sx={{
              p: 1.8,
              borderRadius: "16px",
              backgroundColor: primaryColor,
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 8px 20px ${primaryColor}40`,
            }}
          >
            <StorageIcon sx={{ fontSize: 34 }} />
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="h4" fontWeight="800" sx={{ color: "text.primary", letterSpacing: "-0.5px" }}>
                Backend Workspace
              </Typography>
              {notificationCount > 0 && (
                <Chip
                  label={`${notificationCount} New`}
                  color="error"
                  size="small"
                  sx={{ fontWeight: 700, borderRadius: "12px", height: 24 }}
                />
              )}
            </Box>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              Review pending API requests, implement endpoints, and deliver to frontend
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3.5 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <ModernStatCard
            title="Pending Requests"
            value={stats.requested}
            icon={<PendingIcon sx={{ fontSize: 28 }} />}
            accentColor="#F59E0B"
            subtext="Requests awaiting development"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <ModernStatCard
            title="In Progress"
            value={stats.inProgress}
            icon={<PlayArrowIcon sx={{ fontSize: 28 }} />}
            accentColor="#3B82F6"
            subtext="Endpoints currently in dev"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <ModernStatCard
            title="Completed Endpoints"
            value={stats.completed}
            icon={<CheckCircleIcon sx={{ fontSize: 28 }} />}
            accentColor="#10B981"
            subtext="Delivered API endpoints"
          />
        </Grid>
      </Grid>

      {/* Tabs & Controls */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: "16px",
          border: "1px solid rgba(226, 232, 240, 0.8)",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.02)",
          backgroundColor: "background.paper",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => {
            setActiveTab(newValue);
            setPage(0);
          }}
          sx={{
            px: 2,
            pt: 1,
            borderBottom: "1px solid #E2E8F0",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.95rem",
              minHeight: 52,
              px: 3,
              borderRadius: "10px 10px 0 0",
              color: "text.secondary",
              "&.Mui-selected": {
                color: primaryColor,
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: primaryColor,
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                Requested
                <Chip
                  label={stats.requested}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    backgroundColor: activeTab === "requested" ? "#FFFBEB" : "#F1F5F9",
                    color: activeTab === "requested" ? "#B45309" : "#64748B",
                  }}
                />
              </Box>
            }
            value="requested"
          />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                In Progress
                <Chip
                  label={stats.inProgress}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    backgroundColor: activeTab === "in_progress" ? "#EFF6FF" : "#F1F5F9",
                    color: activeTab === "in_progress" ? "#1D4ED8" : "#64748B",
                  }}
                />
              </Box>
            }
            value="in_progress"
          />
        </Tabs>

        {/* Filters */}
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                placeholder="Search by component, task, lead, or endpoint..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  label="Priority"
                  onChange={(e) => {
                    setPriorityFilter(e.target.value as Priority | "all");
                    setPage(0);
                  }}
                  sx={{ borderRadius: "12px" }}
                >
                  <MenuItem value="all">All Priorities</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => {
                  setPriorityFilter("all");
                  setSearchTerm("");
                  setPage(0);
                }}
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  py: 0.8,
                  borderColor: "#CBD5E1",
                  color: "text.secondary",
                  "&:hover": { borderColor: "#94A3B8" },
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "18px",
          border: "1px solid rgba(226, 232, 240, 0.9)",
          boxShadow: "0 4px 20px -2px rgba(0,0,0,0.04)",
          overflow: "hidden",
          backgroundColor: "background.paper",
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Component</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Task Description</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Requested By</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Endpoint</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Method</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }} align="center">Image</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <Typography variant="body1" fontWeight="600" color="text.secondary">
                      No backend requests matching criteria
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRequests.map((request) => (
                  <TableRow
                    key={request.id || `request-${request.component}-${request.task}`}
                    hover
                    sx={{
                      transition: "background-color 0.15s ease",
                      "&:hover": { backgroundColor: "#F1F5F9" },
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" fontWeight="700" color="text.primary">
                        {request.component}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      <Tooltip title={request.task}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 260,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: "text.primary",
                          }}
                        >
                          {request.task}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar
                          sx={{
                            width: 26,
                            height: 26,
                            fontSize: "0.75rem",
                            backgroundColor: `${primaryColor}20`,
                            color: primaryColor,
                            fontWeight: 700,
                          }}
                        >
                          {(request.createdBy || request.lead || "U").charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2" color="text.secondary" fontWeight="500">
                          {request.createdBy || request.lead || "Unknown"}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      {request.endpoint ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CodeIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "'Fira Code', 'Roboto Mono', monospace",
                              fontSize: "0.78rem",
                              maxWidth: 180,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {request.endpoint}
                          </Typography>
                        </Box>
                      ) : (
                        <Chip
                          label="Unassigned"
                          size="small"
                          sx={{
                            backgroundColor: "#FFFBEB",
                            color: "#B45309",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            borderRadius: "8px",
                          }}
                        />
                      )}
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      <ModernMethodChip method={request.method} />
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      <ModernPriorityChip priority={request.priority} />
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" fontSize="0.8rem" color="text.secondary">
                        {request.requestDate ? new Date(request.requestDate).toLocaleDateString() : "N/A"}
                      </Typography>
                    </TableCell>

                    <TableCell align="center" sx={{ py: 2 }}>
                      {request.imageUrl ? (
                        <Tooltip title="View Attachment Image">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (request.imageUrl) {
                                setPreviewImageUrl(request.imageUrl);
                                setImagePreviewOpen(true);
                              }
                            }}
                            sx={{
                              color: primaryColor,
                              backgroundColor: `${primaryColor}10`,
                              "&:hover": { backgroundColor: `${primaryColor}25` },
                            }}
                          >
                            <ImageIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.6 }}>
                          —
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="center" sx={{ py: 2 }}>
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                        {request.status === "pending" && (
                          <Tooltip title="Start Working (In Progress)">
                            <IconButton
                              size="small"
                              onClick={() => handleStartWork(request)}
                              sx={{
                                color: "#3B82F6",
                                backgroundColor: "#EFF6FF",
                                "&:hover": { backgroundColor: "#DBEAFE" },
                              }}
                            >
                              <PlayArrowIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(request.status === "pending" || request.status === "in_progress") && (
                          <Tooltip title="Approve & Complete Endpoint">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenApproveDialog(request)}
                              sx={{
                                color: "#10B981",
                                backgroundColor: "#ECFDF5",
                                "&:hover": { backgroundColor: "#D1FAE5" },
                              }}
                            >
                              <ApproveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => onViewRequest(request)}
                            sx={{
                              color: primaryColor,
                              backgroundColor: `${primaryColor}10`,
                              "&:hover": { backgroundColor: `${primaryColor}25` },
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredRequests.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ borderTop: "1px solid #E2E8F0" }}
        />
      </Paper>

      {/* Approve & Complete Dialog */}
      <Dialog
        open={openApproveDialog}
        onClose={() => {
          setOpenApproveDialog(false);
          setSelectedRequest(null);
          setEndpointUrl("");
          setStatusCode(undefined);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            p: 1,
            boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: "12px",
                backgroundColor: "#ECFDF5",
                color: "#047857",
                display: "flex",
              }}
            >
              <ApproveIcon />
            </Box>
            <Typography variant="h6" fontWeight="700">
              Approve & Deliver API Endpoint
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <>
              <Alert severity="info" sx={{ mb: 2.5, borderRadius: "12px" }}>
                Once completed, the API endpoint URL will be delivered to frontend developers.
              </Alert>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 2.5,
                  borderRadius: "12px",
                  backgroundColor: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                }}
              >
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="600">
                      COMPONENT
                    </Typography>
                    <Typography variant="body1" fontWeight="700">
                      {selectedRequest.component}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="600">
                      HTTP METHOD
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <ModernMethodChip method={selectedRequest.method} />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="600">
                      TASK DESCRIPTION
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {selectedRequest.task}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              <TextField
                fullWidth
                label="API Endpoint URL *"
                value={endpointUrl}
                onChange={(e) => setEndpointUrl(e.target.value)}
                placeholder="/api/v1/resource/endpoint-path/"
                required
                sx={{ mb: 2.5, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CodeIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                helperText="Enter the complete endpoint URL path for frontend consumers"
              />

              <TextField
                fullWidth
                label="HTTP Status Code (Optional)"
                type="number"
                value={statusCode || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    const parsed = parseInt(value, 10);
                    setStatusCode(!isNaN(parsed) ? parsed : undefined);
                  } else {
                    setStatusCode(undefined);
                  }
                }}
                placeholder="200"
                sx={{ mb: 2.5, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                inputProps={{ min: 100, max: 599 }}
                helperText="Expected success status code (e.g., 200, 201)"
              />

              {selectedRequest.imageUrl && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
                    Attached UI Context
                  </Typography>
                  <Box
                    component="img"
                    src={selectedRequest.imageUrl}
                    alt="Endpoint context attachment"
                    onClick={() => {
                      if (selectedRequest.imageUrl) {
                        setPreviewImageUrl(selectedRequest.imageUrl);
                        setImagePreviewOpen(true);
                      }
                    }}
                    sx={{
                      width: "100%",
                      maxHeight: 240,
                      objectFit: "contain",
                      borderRadius: "12px",
                      border: "1px solid #E2E8F0",
                      cursor: "pointer",
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button
            onClick={() => {
              setOpenApproveDialog(false);
              setSelectedRequest(null);
              setEndpointUrl("");
              setStatusCode(undefined);
            }}
            sx={{ borderRadius: "10px", color: "text.secondary", textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApproveAndComplete}
            variant="contained"
            disabled={!endpointUrl || endpointUrl.trim() === ""}
            startIcon={<ApproveIcon />}
            sx={{
              backgroundColor: primaryColor,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              px: 3,
            }}
          >
            Approve & Deliver Endpoint
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview */}
      <Dialog
        open={imagePreviewOpen}
        onClose={() => {
          setImagePreviewOpen(false);
          setPreviewImageUrl(null);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}
      >
        <DialogTitle sx={{ backgroundColor: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight="700">
              Attachment Preview
            </Typography>
            <IconButton
              onClick={() => {
                setImagePreviewOpen(false);
                setPreviewImageUrl(null);
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, textAlign: "center", backgroundColor: "#0F172A" }}>
          {previewImageUrl && (
            <Box
              component="img"
              src={previewImageUrl}
              alt="Endpoint context attachment"
              sx={{
                maxHeight: "75vh",
                maxWidth: "100%",
                objectFit: "contain",
                borderRadius: "12px",
                boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
